import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import AvatarHomeHero from './components/AvatarHomeHero';
import About from './components/About';
import Technologies from './components/Technologies';
import CaseStudiesSection from './components/CaseStudies/CaseStudiesSection';
import FooterCTA from './components/FooterCTA';
import Footer from './components/Footer';
import InvestorLogin from './components/InvestorRelations/InvestorLogin';
import { LoadingScreen } from './components/Loading/LoadingScreen';
import SignalPage from './components/Signal/SignalPage';
import HarmonizePage from './components/Harmonize/HarmonizePage';
import TwitchCaseStudy from './components/CaseStudies/TwitchCaseStudy';
import ConsciousnessExplorationCaseStudy from './components/CaseStudies/ConsciousnessExplorationCaseStudy';
import SportsCoachingCaseStudy from './components/CaseStudies/SportsCoachingCaseStudy';
import UBICaseStudy from './components/CaseStudies/UBICaseStudy';
import CultivateCoinCaseStudy from './components/CaseStudies/CultivateCoinCaseStudy';
import ReflectCaseStudy from './components/CaseStudies/ReflectCaseStudy';
import InTheLabCaseStudy from './components/CaseStudies/InTheLabCaseStudy';
import DAOAnnouncementPage from './components/DAO/DAOAnnouncementPage';
import ConstitutionPage from './components/DAO/ConstitutionPage';
import VotingPage from './components/DAO/VotingPage';
import BloodPage from './components/Products/Blood/BloodPage';
import CerebralOSPage from './components/Products/CerebralOS/CerebralOSPage';
import EssencePage from './components/Products/Essence/EssencePage';
import InnerVoicePage from './components/Products/InnerVoice/InnerVoicePage';
import AuraResonantLayerPage from './components/Products/AuraResonantLayer/AuraResonantLayerPage';
import Modal from './components/shared/Modal';
import ContactForm from './components/forms/ContactForm';
import PartnerForm from './components/forms/PartnerForm';
import HarmonyAIPage from './components/AI/HarmonyAIPage';
import SelfPhonePage from './components/SelfPhone/SelfPhonePage';
import FlashKitPage from './components/SelfPhone/FlashKitPage';
import OnboardingPage from './components/Flash/OnboardingPage';
import FlashDashboard from './components/Flash/FlashDashboard';
import TechGuidePage from './components/Flash/TechGuidePage';
import { FlashProvider } from './contexts/FlashContext';
import { StripeProvider } from './contexts/StripeContext';
import { ApolloProvider } from './contexts/ApolloContext';
import ScrollToTop from './components/utils/ScrollToTop';
import NotFound from './components/NotFound';
import WhitePaperPage from './components/SelfPhone/WhitePaperPage';
import PrivacyPolicyPage from './pages/PrivacyPolicy';
import TermsPage from './components/SelfPhone/TermsPage';
import SignalEconomyPage from './components/SelfPhone/SignalEconomyPage';
import SignalValueCalculator from './components/SignalValueCalculator';
import GlobalSignalPage from './pages/features/GlobalSignal';
import PersonalAIVoicePage from './pages/features/PersonalAIVoice';
import AgentCommunicationPage from './pages/features/AgentCommunication';
import SignalProtectionPage from './pages/features/SignalProtection';
import RoleModelAIPage from './pages/features/RoleModelAI';
import HarmonySignalEnginePage from './pages/HarmonySignalEngine';
import HarmonyLitepaper from './pages/HarmonyLitepaper';
import AuraShieldPage from './pages/features/AuraShield';
import TermsAndConditionsPage from './pages/TermsAndConditions';
import ResellerAgreementPage from './pages/ResellerAgreement';
import MeshLicensePage from './pages/MeshLicense';
import DeveloperAgreementPage from './pages/DeveloperAgreement';
import SignalMeshFeesPage from './pages/SignalMeshFees';
import EssenceDisclaimerPage from './pages/EssenceDisclaimer';
import WarrantyPolicyPage from './pages/WarrantyPolicy';
import DataMonetizationPolicyPage from './pages/DataMonetizationPolicy';
import CultivateCoinMint from './components/CultivateCoinMint';
import ReflectMint from './components/ReflectMint';
import ResonantCore from './components/CaseStudies/ResonantCore';
import AllProducts from './pages/AllProducts';
import ResonatiaPage from './pages/Resonatia';
import WhatIsThisPage from './components/SignalCalculator/WhatIsThisPage';
import WhyDataCanReplaceMoneyPage from './components/SignalCalculator/WhyDataCanReplaceMoneyPage';
import WhyKilobytePricelessPage from './components/SignalCalculator/WhyKilobytePricelessPage';
import HowSignalEconomyWorksPage from './components/SignalCalculator/HowSignalEconomyWorksPage';
import WhyEssenceDollarsPage from './components/SignalCalculator/WhyEssenceDollarsPage';
import ForAICompaniesPage from './components/SignalCalculator/ForAICompaniesPage';
import EssenceServicesPage from './components/Services/EssenceServicesPage';
import SuccessPage from './pages/SuccessPage';
import SmartChoiceWaterCaseStudy from './components/CaseStudies/SmartChoiceWaterCaseStudy';
import EventfixCaseStudy from './components/CaseStudies/EventfixCaseStudy';
import ModuulizeCaseStudy from './components/CaseStudies/ModuulizeCaseStudy';
import LivingWebsitePage from './pages/LivingWebsitePage';
import IncubatorPage from './pages/IncubatorPage';
import ConsultationPage from './pages/ConsultationPage';
import BuildPage from './pages/BuildPage';
import CareersPage from './pages/CareersPage';
import RoboticsPage from './pages/RoboticsPage';
import RoboticsProductPage from './pages/RoboticsProductPage';
import BuildQuotePage from './pages/BuildQuotePage';
import Team from './pages/Team';
import ManifestAvatarEmbed from './components/ManifestAvatarEmbed';
import AvatarSiteAwareLayer from './components/AvatarSiteAwareLayer';
import ManifestAvatarContentBridge from './components/ManifestAvatarContentBridge';
import AeoGlobalHead from './components/AeoGlobalHead';
import { isEmbeddedSiteView, markEmbeddedSiteView } from './lib/avatarEmbed';

interface HomePageProps {
  onContactClick: () => void;
  onPartnerClick: () => void;
}

function HomePage({ onContactClick, onPartnerClick }: HomePageProps) {
  if (isEmbeddedSiteView()) return null;

  return (
    <>
      <LoadingScreen />
      <AvatarHomeHero
        onContactClick={onContactClick}
        onPartnerClick={onPartnerClick}
      />
    </>
  );
}

function App() {
  const [showInvestorLogin, setShowInvestorLogin] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<string | null>(null);

  useEffect(() => {
    markEmbeddedSiteView();
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <AeoGlobalHead />
        <StripeProvider>
          <ApolloProvider>
            <FlashProvider>
              <div className="min-h-screen bg-gradient-to-b from-[#0A0A0B] via-black to-[#0A0A0B] text-white">
                <Navbar />
                
                {/* Modals - Available across all routes */}
                <Modal isOpen={showContactModal} onClose={() => setShowContactModal(false)} title="Contact Us">
                  <ContactForm />
                </Modal>
                <Modal isOpen={showPartnerModal} onClose={() => setShowPartnerModal(false)} title="Partner With Us">
                  <PartnerForm />
                </Modal>
                {showInvestorLogin && <InvestorLogin onBack={() => setShowInvestorLogin(false)} />}

                <ManifestAvatarEmbed />
                <AvatarSiteAwareLayer />
                <ManifestAvatarContentBridge
                  onContactClick={() => setShowContactModal(true)}
                  onPartnerClick={() => setShowPartnerModal(true)}
                />

                <Routes>
                  <Route 
                    path="/" 
                    element={
                      <HomePage 
                        onContactClick={() => setShowContactModal(true)}
                        onPartnerClick={() => setShowPartnerModal(true)}
                      />
                    } 
                  />
                  
                  {/* Main Section Routes */}
                  <Route path="/products" element={<AllProducts />} />
                  <Route path="/case-studies" element={<CaseStudiesSection />} />
                  <Route path="/dao" element={<DAOAnnouncementPage />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/careers" element={<CareersPage />} />
                  <Route path="/technologies" element={<Technologies />} />
                  
                  {/* Products Routes */}
                  <Route path="/living-website" element={<LivingWebsitePage />} />
                  <Route path="/incubator" element={<IncubatorPage />} />
                  <Route path="/consultation" element={<ConsultationPage />} />
                  <Route path="/build" element={<BuildPage />} />
                  <Route path="/build/quote/:id" element={<BuildQuotePage />} />
                  <Route path="/role-model-ai" element={<RoleModelAIPage />} />
                  <Route path="/signal" element={<SignalPage />} />
                  <Route path="/harmonize" element={<HarmonizePage />} />
                  <Route path="/robotics" element={<RoboticsPage />} />
                  <Route path="/robotics/:productId" element={<RoboticsProductPage />} />
                  <Route path="/blood" element={<BloodPage />} />
                  <Route path="/cerebral-os" element={<CerebralOSPage />} />
                  <Route path="/essence" element={<EssencePage />} />
                  <Route path="/inner-voice" element={<InnerVoicePage />} />
                  <Route path="/resonatia" element={<ResonatiaPage />} />
                  <Route path="/aura-resonant-layer" element={<AuraResonantLayerPage />} />
                  <Route path="/harmony-ai" element={<HarmonyAIPage />} />
                  
                  {/* SELF Phone Routes */}
                  <Route path="/self-phone" element={<SelfPhonePage />} />
                  <Route path="/flash" element={<FlashKitPage />} />
                  <Route path="/success" element={<SuccessPage />} />
                  <Route path="/self-phone/white-paper" element={<WhitePaperPage />} />
                  <Route path="/self-phone/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/self-phone/terms" element={<TermsPage />} />
                  <Route path="/flash/signal-economy" element={<SignalEconomyPage />} />
                  <Route path="/flash/signal-calculator" element={<SignalValueCalculator />} />
                  <Route path="/flash/signal-calculator/what-is-this" element={<WhatIsThisPage />} />
                  <Route path="/flash/signal-calculator/why-data-can-replace-money" element={<WhyDataCanReplaceMoneyPage />} />
                  <Route path="/flash/signal-calculator/why-kilobyte-priceless" element={<WhyKilobytePricelessPage />} />
                  <Route path="/flash/signal-calculator/how-signal-economy-works" element={<HowSignalEconomyWorksPage />} />
                  <Route path="/flash/signal-calculator/why-essence-dollars" element={<WhyEssenceDollarsPage />} />
                  <Route path="/flash/signal-calculator/for-ai-companies" element={<ForAICompaniesPage />} />
                  <Route path="/flash/services" element={<EssenceServicesPage />} />
                  <Route path="/flash/onboarding" element={<OnboardingPage />} />
                  <Route path="/flash/dashboard" element={<FlashDashboard />} />
                  <Route path="/flash/docs/tech-guide" element={<TechGuidePage />} />
                  
                  {/* Case Studies Routes */}
                  <Route path="/case-studies/moduulize" element={<ModuulizeCaseStudy />} />
                  <Route path="/case-studies/eventfix" element={<EventfixCaseStudy />} />
                  <Route path="/case-studies/twitch" element={<TwitchCaseStudy />} />
                  <Route path="/case-studies/consciousness" element={<ConsciousnessExplorationCaseStudy />} />
                  <Route path="/case-studies/sports" element={<SportsCoachingCaseStudy />} />
                  <Route path="/case-studies/ubi" element={<UBICaseStudy />} />
                  <Route path="/cultivatecoin" element={<CultivateCoinCaseStudy />} />
                  <Route path="/case-studies/cultivate" element={<Navigate to="/cultivatecoin" replace />} />
                  <Route path="/case-studies/reflect" element={<ReflectCaseStudy />} />
                  <Route path="/case-studies/resonant-core" element={<ResonantCore />} />
                  <Route path="/case-studies/in-the-lab" element={<InTheLabCaseStudy />} />
                  <Route path="/case-studies/smart-choice-water" element={<SmartChoiceWaterCaseStudy />} />
                  <Route path="/cultivate-mint" element={<CultivateCoinMint />} />
                  <Route path="/reflect-mint" element={<ReflectMint />} />
                  
                  {/* DAO Routes */}
                  <Route path="/dao/announcement" element={<DAOAnnouncementPage />} />
                  <Route path="/dao/constitution" element={<ConstitutionPage />} />
                  <Route path="/dao/voting" element={<VotingPage />} />

                  {/* Feature Pages */}
                  <Route path="/features/role-model-ai" element={<RoleModelAIPage />} />
                  <Route path="/features/global-signal" element={<GlobalSignalPage />} />
                  <Route path="/features/personal-ai-voice" element={<PersonalAIVoicePage />} />
                  <Route path="/features/agent-communication" element={<AgentCommunicationPage />} />
                  <Route path="/features/signal-protection" element={<SignalProtectionPage />} />
                  <Route path="/features/aura-shield" element={<AuraShieldPage />} />

                  {/* New HarmonySignalEngine route */}
                  <Route path="/signal-engine" element={<HarmonySignalEnginePage />} />

                  {/* New HarmonyLitepaper route */}
                  <Route path="/harmony-litepaper" element={<HarmonyLitepaper />} />

                  {/* Legal Pages */}
                  <Route path="/terms" element={<TermsAndConditionsPage />} />
                  <Route path="/privacy" element={<PrivacyPolicyPage />} />
                  <Route path="/reseller-agreement" element={<ResellerAgreementPage />} />
                  <Route path="/mesh-license" element={<MeshLicensePage />} />
                  <Route path="/developer-agreement" element={<DeveloperAgreementPage />} />
                  <Route path="/signal-mesh-fees" element={<SignalMeshFeesPage />} />
                  <Route path="/essence-disclaimer" element={<EssenceDisclaimerPage />} />
                  <Route path="/warranty-policy" element={<WarrantyPolicyPage />} />
                  <Route path="/data-monetization-policy" element={<DataMonetizationPolicyPage />} />

                  <Route path="/ayota-marketplace" element={<Navigate to="/role-model-ai" replace />} />
                  <Route path="/concierge-ai" element={<Navigate to="/role-model-ai" replace />} />

                  {/* Team route */}
                  <Route path="/team" element={<Team />} />

                  {/* Catch-all route for 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
            </FlashProvider>
          </ApolloProvider>
        </StripeProvider>
      </Router>
    </HelmetProvider>
  );
}

export default App;